import type { CSSProperties } from 'react'
import { Input } from '@chakra-ui/react'
import React from 'react'
import type { StylesConfig } from 'react-windowed-select'
import type BaseReactSelect from 'react-select'
import Select, { WindowedMenuList, components, createFilter } from 'react-windowed-select'
import { colors } from '../theme/styles'

const CustomOption = (incomingProps: any) => {
  const { children, ...props } = incomingProps
  const { onMouseMove, onMouseOver, ...innerProps } = props.innerProps
  const newProps = { ...props, innerProps: innerProps }
  return <components.Option {...newProps}>{children}</components.Option>
}

const Placeholder = (props: any) => (
  <components.Placeholder {...props}>
    {props.selectProps.placeholder}
  </components.Placeholder>
)

const customStyles: StylesConfig = {
  container: (provided, state: any) => ({
    ...provided,
    width: state.selectProps.width,
  }),
  option: provided => ({
    ...provided,
    color: colors.appDarkIndigo,
    fontSize: '14px',
    fontWeight: 'normal',
    fontStretch: 'normal',
    fontStyle: 'normal',
    lineHeight: 1.5,
    letterSpacing: 'normal',
    textAlign: 'left',
  }),
  menu: provided => ({
    ...provided,
    marginTop: '6px',
    borderRadius: '5px',
    border: `solid 0.5px ${colors.appGreyFour}`,
    backgroundColor: colors.appWhite,
    boxShadow: 'none',
  }),
  control: (provided, state: any) => ({
    ...provided,
    alignItems: 'center',
    '&:hover': {
      borderColor: state.menuIsOpen ? colors.appClearBlue : colors.appGreyFour,
    },
    minHeight: state.selectProps.variant === 'compact' ? '35px' : '150px',
    maxHeight: state.selectProps.variant === 'compact' ? '150px' : 'initial',
    height: state.selectProps.variant === 'compact' ? 'auto' : '150px',
    margin: '0px',
    overflowY: 'auto',
    borderRadius: '5px',
    border: 'solid 0.5px',
    borderColor: state.menuIsOpen ? colors.appClearBlue : colors.appGreyThree,
    boxShadow: 'none',
    backgroundColor: colors.appWhite,
    fontSize: '14px',
    fontWeight: 'normal',
    fontStretch: 'normal',
    fontStyle: 'normal',
    lineHeight: 1.5,
    letterSpacing: 'normal',
    textAlign: 'left',
    color: state.menuIsOpen ? colors.appClearBlue : colors.appGreyThree,
  }),
  dropdownIndicator: (provided, state: any) => ({
    ...provided,
    color: state.menuIsOpen ? colors.appSlate : colors.appGreyFour,
    padding: '6px',
  }),
  indicatorSeparator: provided => ({
    ...provided,
    color: colors.appGreyFour,
  }),
  multiValueLabel: (provided, state: any) => ({
    ...provided,
    height: state.selectProps.variant === 'compact' ? '25px' : '36px',
    padding:
      state.selectProps.variant === 'compact'
        ? '1px 10px 1px 10px'
        : '7px 10px 7px 10px',
    backgroundColor: colors.appWhite,
    fontSize: '14px',
    fontStretch: 'normal',
    fontStyle: 'normal',
    lineHeight: 1.5,
    letterSpacing: 'normal',
    color: colors.appSlate,
    border: `1px solid ${colors.appPaleGreyFour}`,
    borderRight: 0,
  }),
  multiValueRemove: (provided, state: any) => ({
    ...provided,
    height: state.selectProps.variant === 'compact' ? '25px' : '36px',
    fontSize: '11px',
    padding: '7px 8.4px 7.4px 9px',
    opacity: 1,
    backgroundColor: colors.appWhite,
    color: colors.appBattleshipGrey,
    border: `1px solid ${colors.appPaleGreyFour}`,
    borderLeft: 0,
  }),
  valueContainer: (provided, state: any) => ({
    ...provided,
    height: state.selectProps.variant === 'compact' ? '100%' : 'auto',
  }),
  clearIndicator: (provided, state: any) =>
    state.selectProps.variant !== 'compact'
      ? {
          ...provided,
          display: 'block',
          margin: '6px 11px 6px 33px',
          fontSize: '14px',
          fontStretch: 'normal',
          fontStyle: 'normal',
          lineHeight: 'normal',
          letterSpacing: 'normal',
          color: colors.appBattleshipGrey,
          '&:hover': {
            color: colors.appSlate,
          },
        }
      : provided,
}

const customStylesForShareModal: StylesConfig = {
  ...customStyles,
  control: (provided, state: any) => ({
    ...provided,
    alignItems: 'center',
    '&:hover': {
      borderColor: state.menuIsOpen ? colors.appClearBlue : colors.appGreyFour,
    },
    minHeight: '35px',
    maxHeight: '60px',
    height: 'auto',
    margin: '0px',
    overflowY: 'auto',
    borderRadius: '5px',
    border: 'solid 0.5px',
    borderColor: state.menuIsOpen ? colors.appClearBlue : colors.appGreyThree,
    boxShadow: 'none',
    backgroundColor: colors.appWhite,
    fontSize: '14px',
    fontWeight: 'normal',
    fontStretch: 'normal',
    fontStyle: 'normal',
    lineHeight: 1.5,
    letterSpacing: 'normal',
    textAlign: 'left',
    color: state.menuIsOpen ? colors.appClearBlue : colors.appGreyThree,
  }),
  dropdownIndicator: (provided, state: any) => ({
    ...provided,
    color: state.menuIsOpen ? colors.appSlate : colors.appGreyFour,
    alignSelf: 'flex-start',
    position: 'sticky',
    top: '25%',
  }),
  clearIndicator: (provided, state: any) => ({
    ...provided,
    alignSelf: 'flex-start',
    position: 'sticky',
    top: '25%',
    color: colors.appBattleshipGrey,
    '&:hover': {
      color: colors.appBlue,
    },
  }),
}

const colourStylesForManageMediaCollection = {
  option: (styles, props) => ({
    ...styles,
    backgroundColor: props.isFocused ? colors.appPaleGreyFour : null,

    ':active': {
      ...styles[':active'],
      backgroundColor: colors.appPaleGreyFour,
    },
  }),
}

type ReactSelectProps = Omit<
  Parameters<typeof BaseReactSelect>[0],
  'onChange' | 'ref'
> &
  Omit<Parameters<typeof BaseReactSelect>[0], 'onChange' | 'ref'>
type SelectReturn = ReturnType<typeof Select>

interface SelectProps {
  ref?: any
  refCallback?: any
  variant?: string
  width?: string
  stylesVariant?: string
  onChange?(value: any): void
}

const ReactSelect = (
  props: ReactSelectProps & SelectProps,
): SelectReturn | React.ReactElement => {
  if (props.isDisabled) {
    let value = ''
    if (props.value) {
      if (props.value instanceof Array) {
        value = props.value.map(x => x.label).join(', ')
      } else {
        value = (props.value as any).label as string
      }
    }

    return <Input value={value} disabled={true} readOnly={true} />
  }

  const switchStatement = stylesVariant => {
    switch (stylesVariant) {
      case 'share_modal_custom':
        return customStylesForShareModal

      case 'media_collection_modal_custom':
        return colourStylesForManageMediaCollection

      default:
        return customStyles
    }
  }

  return (
    <Select
      {...props}
      windowThreshold={100}
      filterOption={createFilter({ignoreAccents: false})}
      ref={props.refCallback}
      styles={switchStatement(props.stylesVariant)}
      // @ts-ignore
      variant={props.variant === 'compact' ? 'compact' : 'default'}
      components={{
        Placeholder,
        Option: CustomOption,
        ...props.components,
      }}
    />
  )
}

export default ReactSelect
